<template>
  <Spinner v-if="loading"></Spinner>
  <div v-else-if="!hasError">
    <NewVehicleDetails
      :change-vehicle-search="changeVehicleSearch"
      @edit-vehicle="editVehicle"
    />

    <TariffInformationTile
      v-if="tariffInformation"
      :tariff-information="tariffInformation!"
    />

    <Card :footerDivider="false" :headerDivider="false">
      <template v-slot:content>
        <InsuranceDataTile v-if="insuranceData" :insurance-data="insuranceData" />
      </template>
    </Card>
    <Card :footerDivider="false" :headerDivider="false">
      <template v-slot:content>
        <InsuranceProtectionTile
          v-if="insuranceProtection && tariffInformation"
          :comprehensionAsString="tariffInformation!.comprehensionAsString!.toString()"
          :insurance-protection="insuranceProtection"
          :tariff="tariffInformation!.tariff!.toString()"
        />
      </template>
    </Card>

    <PartnerAddressComp
      :policy-id="policyId"
      lines-whitelist="academicTitle, nobilityTitle, firstName, lastName, nameAffix, dateOfBirth, streetAndHouseNumber, additionalToAddress, zipcodeAndCity, country"
      use-collapser="true"
    />

    <Card :footerDivider="false" :headerDivider="false" dockedPosition="top">
      <template v-slot:content>
        <DocumentRequestTile
          :documents-sent="documentsSent"
          :policy-id="policyId"
          docked-position="middle"
          @documents-requested="documentsRequested"
          @error:documents-requested="hasError = true"
        />
        <StaticCustomerInformationTile />
      </template>
    </Card>

    <Card :footerDivider="false" :headerDivider="false" dockedPosition="bottom">
      <template v-slot:content>
        <Alert v-if="!documentsSent">
          <template v-slot:main>
            <div class="text">{{ $t('newContractDetails.documentNotice.title') }}</div>
            <div class="text alert-regular-font">
              {{ $t('newContractDetails.documentNotice.helptext') }}
            </div>
          </template>
        </Alert>
      </template>
      <template #footer>
        <div class="footer-cta footer-cta--left">
          <CustomButton
            :disabledState="!documentsSent"
            :buttonColor="ButtonColor.PRIMARY"
            :button-size="ButtonSize.BIG"
            buttonId="conclusionButton"
            @click="conclude"
          >
            {{ $t('newContractDetails.conclude') }}
          </CustomButton>
          <CustomButton
            :buttonColor="ButtonColor.LINK"
            buttonId="abortButton"
            @click="abort"
          >
            {{ $t('newContractDetails.abort') }}
          </CustomButton>
        </div>
      </template>
    </Card>
  </div>
  <CallCenterTile v-else></CallCenterTile>
</template>

<script setup lang="ts">
import { onMounted, type PropType, type Ref, ref } from 'vue'
import {
  Alert,
  ButtonColor,
  ButtonSize,
  Card,
  CustomButton,
  Spinner
} from 'de-zurich-ui-library'
import { policyIdReg } from '@/utilsTenant/PropsValidation'
import { type ChangeVehicleSearch } from '@/typesTenant/VehicleSelectionApi'
import {
  type ChangeVehicleApplication,
  type ChangeVehicleConclusion,
  type CircleOfDrivers,
  CircleOfDriversType,
  type CompleteChangeVehicleOffer,
  type ComprehensiveInsurance,
  ComprehensiveInsuranceType,
  Gender,
  type InsuranceData,
  type InsuranceProtection,
  type MotorConclusionResponse,
  type PaymentPeriod,
  Product,
  type TariffInformation
} from '@/typesTenant/NewVehicleDetailApi'
import { t } from '@/i18n'
import { formatDate } from '@/utils/policy/PolicyUtils'
import { formatCurrency, formatKilometers } from '@/utils/motor/MotorDataBoxDataUtils'
import { type MonetaryAmount } from 'policies-api'
import { type PassengerInsurance } from '@/types/motor/MotorInsurance'
import PartnerAddressComp from '@/components/partner-proxy/PartnerAddressComp.vue'
import CallCenterTile from '@/componentsTenant/motor/vehiclechange/CallCenterTile.vue'
import NewVehicleDetails from '@/componentsTenant/motor/vehiclechange/newdetails/NewVehicleDetails.vue'
import TariffInformationTile from '@/componentsTenant/motor/vehiclechange/newdetails/TariffInformationTile.vue'
import InsuranceDataTile from '@/componentsTenant/motor/vehiclechange/newdetails/InsuranceDataTile.vue'
import InsuranceProtectionTile from '@/componentsTenant/motor/vehiclechange/newdetails/InsuranceProtectionTile.vue'
import DocumentRequestTile from '@/componentsTenant/motor/vehiclechange/newdetails/DocumentRequestTile.vue'
import StaticCustomerInformationTile from '@/componentsTenant/motor/vehiclechange/newdetails/StaticCustomerInformationTile.vue'
import {
  fetchVehicleChangeQuotes,
  submitMotorConclusion
} from '@/storeTenant/motorServiceTenant'

const props = defineProps({
  policyId: {
    type: String,
    required: true,
    validator(value: string): boolean {
      return policyIdReg.test(value)
    }
  },
  changeVehicleSearch: {
    type: Object as PropType<ChangeVehicleSearch>,
    required: true
  }
})

const emit = defineEmits<{
  'edit-vehicle': [none: void]
  'scroll-to-top': [none: void]
  abort: [none: void]
  conclude: [motorConclusionResponse: MotorConclusionResponse]
}>()

const NUMBER_OF_FULLY_COMPREHENSIVE_INSURANCE_CLAIMS_CURRENT_YEAR = 0
const NUMBER_OF_LIABILITY_CLAIMS_CURRENT_YEAR = 0

const loading = ref(false)
const hasError = ref(false)

const documentsSent = ref(false)

const circleOfDrivers: Ref<CircleOfDrivers | null> = ref(null)
const insuranceData: Ref<InsuranceData | null> = ref(null)
const insuranceProtection: Ref<InsuranceProtection | null> = ref(null)
const quoteDate = ref('')
const tariffInformation: Ref<TariffInformation | null> = ref(null)

onMounted(() => {
  loading.value = true
  circleOfDrivers.value = getCircleOfDrivers()

  const body: ChangeVehicleApplication = {
    policyId: props.policyId,
    insuranceStartsAt: props.changeVehicleSearch.desiredStartDate,
    vehicleMileage: props.changeVehicleSearch.mileageAtTheBeginning,
    vehicleFirstRegistration: props.changeVehicleSearch.vehicleFirstRegistration,
    vehicleAcquiredAt: props.changeVehicleSearch.vehicleFirstRegistrationForPartner,
    vehicleTypeKey: props.changeVehicleSearch.typeKey,
    vehicleManufacturerId: props.changeVehicleSearch.manufacturerId,
    numberOfFullyComprehensiveInsuranceClaimsCurrentYear:
      NUMBER_OF_FULLY_COMPREHENSIVE_INSURANCE_CLAIMS_CURRENT_YEAR,
    numberOfLiabilityClaimsCurrentYear: NUMBER_OF_LIABILITY_CLAIMS_CURRENT_YEAR,
    circleOfDrivers: circleOfDrivers.value
  }

  fetchVehicleChangeQuotes(body)
    .then((changeVehicleOffer) => {
      extractDataFromOffer(changeVehicleOffer)
    })
    .catch(() => {
      hasError.value = true
    })
    .finally(() => {
      loading.value = false
    })
})

function getCircleOfDrivers(): CircleOfDrivers {
  let circleOfDrivers: CircleOfDrivers
  if (hasOthersDriving()) {
    circleOfDrivers = getCircleOfDriversVnAndOthers()
  } else if (hasPartnerDriving()) {
    circleOfDrivers = getCircleOfDriversVnAndPartner()
  } else {
    circleOfDrivers = getCircleOfDriversOnlyVn()
  }
  return circleOfDrivers
}

function getCircleOfDriversOnlyVn(): CircleOfDrivers {
  return { circleOfDriversType: CircleOfDriversType.EINZELFAHRER }
}

function getCircleOfDriversVnAndOthers(): CircleOfDrivers {
  return {
    circleOfDriversType: CircleOfDriversType.VN_UND_ANDERE,
    oldestDriverBirthyear: props.changeVehicleSearch.birthyearOldestDriver,
    youngestDriverBirthyear: props.changeVehicleSearch.birthyearYoungestDriver,
    youngestDriverGender: Gender.MALE
  }
}

function getCircleOfDriversVnAndPartner(): CircleOfDrivers {
  return {
    circleOfDriversType: CircleOfDriversType.VN_UND_PARTNER,
    partnerBirthyear: props.changeVehicleSearch.birthyearPartner,
    partnerGender: Gender.MALE
  }
}

function hasPartnerDriving(): boolean {
  return props.changeVehicleSearch.birthyearPartner.length !== 0
}

function hasOthersDriving(): boolean {
  return (
    props.changeVehicleSearch.birthyearOldestDriver.length !== 0 ||
    props.changeVehicleSearch.birthyearYoungestDriver.length !== 0
  )
}

function extractDataFromOffer(changeVehicleOffer: CompleteChangeVehicleOffer): void {
  extractTariffInformation(changeVehicleOffer)
  extractInstanceData(changeVehicleOffer, props.changeVehicleSearch)
  extractInstanceProtection(changeVehicleOffer)
  extractQuoteDate(changeVehicleOffer)
}

function extractInstanceData(
  changeVehicleOffer: CompleteChangeVehicleOffer,
  changeVehicleUserInput: ChangeVehicleSearch
) {
  insuranceData.value = {
    manufacturer: changeVehicleUserInput.vehicleSearchResult?.manufacturer,
    modelType: changeVehicleUserInput.vehicleSearchResult?.modelType,
    manufacturerId: changeVehicleUserInput.vehicleSearchResult?.manufacturerId,
    typeKey: changeVehicleUserInput.vehicleSearchResult?.typeKey,
    power: String(
      t('newContractDetails.insuranceData.kilowattAndPs', {
        kw: changeVehicleUserInput.vehicleSearchResult?.kilowatt,
        ps: changeVehicleUserInput.vehicleSearchResult?.horsepower
      })
    ),
    vehicleFirstRegistration: formatDate(changeVehicleUserInput.vehicleFirstRegistration),
    vehicleFirstRegistrationForPartner: formatDate(
      changeVehicleUserInput.vehicleFirstRegistrationForPartner
    ),
    vehicleDriver: String(
      t(
        'newContractDetails.circleOfDrivers.' +
          changeVehicleOffer.motorQuote.motorApplication.insuranceCover
            .insuredVehicleUsage.drivers.circleOfDriversType
      )
    ),
    usageOfVehicle: String(
      t(
        'newContractDetails.typeOfUse.' +
          changeVehicleOffer.motorQuote.motorApplication.insuranceCover
            .insuredVehicleUsage.typeOfUse
      )
    ),
    kilometersAYear: formatKilometers(
      changeVehicleOffer.motorQuote.motorApplication.insuranceCover.insuredVehicleUsage
        .mileageAYear
    ),
    nightlyParkingSpace: String(
      t(
        'newContractDetails.nightlyParkingSpace.' +
          changeVehicleOffer.motorQuote.motorApplication.insuranceCover
            .insuredVehicleUsage.nightlyParkingPlace
      )
    ),
    numberOfDamages: formatNumberOfDamages(
      changeVehicleOffer.motorQuote.motorApplication.previousContract
        .numberOfLiabilityClaimsLastYear,
      changeVehicleOffer.motorQuote.motorApplication.previousContract
        .numberOfLiabilityClaimsCurrentYear
    ),
    seasonVehicleLicence: String(
      t(
        'newContractDetails.yesNo.' +
          (changeVehicleOffer.motorQuote.motorApplication.insuranceCover.seasonalInsurance
            ? 'booleanTrue'
            : 'booleanFalse')
      )
    ),
    commencementOfInsurance: formatDate(
      changeVehicleOffer.motorQuote.motorApplication.insuranceCover.insuranceStartsAt
    ),
    noClaimsClass:
      changeVehicleOffer.motorQuote.motorApplication.insuranceCover.motorVehicleLiability
        .noClaimsClass
  }
}

function extractInstanceProtection(changeVehicleOffer: CompleteChangeVehicleOffer) {
  let passengerInsurance: PassengerInsurance | undefined
  if (changeVehicleOffer.motorQuote.motorApplication.insuranceCover.passengerInsurance) {
    passengerInsurance = {
      claims:
        changeVehicleOffer.motorQuote.motorApplication.insuranceCover.passengerInsurance,
      amount: changeVehicleOffer.motorQuote.monetaryAmountPassengersInsurance
    }
  }

  let comprehensiveInsuranceType
  if (
    changeVehicleOffer.motorQuote.motorApplication.insuranceCover.comprehensiveInsurance
  ) {
    comprehensiveInsuranceType =
      changeVehicleOffer.motorQuote.motorApplication.insuranceCover
        .comprehensiveInsurance['@type']
  }
  insuranceProtection.value = {
    overallAmount: changeVehicleOffer.motorQuote.premiumRate,
    tariffAmount: changeVehicleOffer.motorQuote.tariffRate,
    assistanceCoverAmount: changeVehicleOffer.motorQuote.monetaryAmountAssistanceCover,
    motorCoverageLiabilityInsurance:
      changeVehicleOffer.motorQuote.motorQuoteLEXLiabilityInsurance,
    motorCoveragePartiallyComprehensiveInsurance:
      changeVehicleOffer.motorQuote.motorQuoteLEXPartiallyComprehensiveInsurance,
    motorCoverageFullyComprehensiveInsurance:
      changeVehicleOffer.motorQuote.motorQuoteLEXFullyComprehensiveInsurance,
    additionalServiceLiabilityInsurance:
      changeVehicleOffer.additionalServices.lexLiabilityInsurances,
    additionalServicePartiallyComprehensiveInsurance:
      changeVehicleOffer.additionalServices.lexPartiallyComprehensiveInsurances,
    additionalServiceFullyComprehensiveInsurance:
      changeVehicleOffer.additionalServices.lexFullyComprehensiveInsurances,
    assistanceCover:
      changeVehicleOffer.motorQuote.motorApplication.insuranceCover.assistanceCover,
    paymentPeriod: changeVehicleOffer.motorQuote.motorApplication.paymentInterval,
    sumInsuredFlatRate: String(
      t('newContractDetails.insuranceProtection.sumInsured', {
        sumInsured: byTheMillions(
          changeVehicleOffer.motorQuote.motorApplication.insuranceCover
            .motorVehicleLiability.sumInsuredFlatRate
        )
      })
    ),
    sumInsuredPerPerson: String(
      t('newContractDetails.insuranceProtection.sumInsured', {
        sumInsured: byTheMillions(
          changeVehicleOffer.motorQuote.motorApplication.insuranceCover
            .motorVehicleLiability.sumInsuredPerPerson
        )
      })
    ),
    passengerInsurance,
    comprehensiveInsuranceType
  }
}

function extractQuoteDate(changeVehicleOffer: CompleteChangeVehicleOffer) {
  quoteDate.value = changeVehicleOffer.motorQuote.quoteDate
}

function byTheMillions(value: MonetaryAmount): number | null {
  if (value.amount) return value ? value.amount / 1000000 : null
  else return null
}

function extractTariffInformation(changeVehicleOffer: CompleteChangeVehicleOffer) {
  const title = getTitle(changeVehicleOffer.motorQuote.motorApplication.paymentInterval)
  const comprehensionAsString = getComprehensionString(
    changeVehicleOffer.motorQuote.motorApplication.insuranceCover.comprehensiveInsurance
  )
  const tariff = getTariff(
    changeVehicleOffer.motorQuote.motorApplication.insuranceCover.product
  )
  const formattedPrice = getFormattedPrice(changeVehicleOffer.motorQuote.premiumRate)

  tariffInformation.value = {
    title,
    tariff,
    comprehensionAsString,
    formattedPrice
  }
}

function getTitle(paymentPeriod: PaymentPeriod): string {
  const translatedPeriod = String(
    t('newContractDetails.tariffInformation.period.' + paymentPeriod)
  )
  return String(t('newContractDetails.newRate', { period: translatedPeriod }))
}

function getComprehensionString(comprehensiveInsurance: ComprehensiveInsurance): string {
  let comprehensionTypeVk: string | undefined
  let comprehensionTypeTk: string | undefined
  if (comprehensiveInsurance) {
    switch (comprehensiveInsurance['@type']) {
      case ComprehensiveInsuranceType.FULL:
        comprehensionTypeVk = String(t('newContractDetails.comprehension.vk'))
        comprehensionTypeTk = String(t('newContractDetails.comprehension.tk'))
        break
      case ComprehensiveInsuranceType.PARTIAL:
        comprehensionTypeTk = String(t('newContractDetails.comprehension.tk'))
        break
    }
  }
  const comprehensionHp = String(t('newContractDetails.comprehension.hp'))

  return [comprehensionTypeVk, comprehensionTypeTk, comprehensionHp]
    .filter(Boolean)
    .join(', ')
}

function getTariff(product: Product): string {
  switch (product) {
    case Product.BASIC:
      return String(t('newContractDetails.tariff.basic'))
    case Product.COMFORT:
      return String(t('newContractDetails.tariff.comfort'))
  }
}

function getFormattedPrice(overallCosts: MonetaryAmount): string {
  return formatCurrency(overallCosts)
}

function formatNumberOfDamages(
  numberOfDamagesLastYear: number,
  numberOfDamagesCurrentYear: number
) {
  const currentYear: number = new Date().getFullYear()
  const lastYear: number = currentYear - 1
  return (
    lastYear +
    ': ' +
    (numberOfDamagesLastYear || String(t('newContractDetails.noClaims'))) +
    '\n' +
    currentYear +
    ': ' +
    (numberOfDamagesCurrentYear || String(t('newContractDetails.noClaims')))
  )
}

function documentsRequested() {
  documentsSent.value = true
}

function conclude() {
  loading.value = true

  const body: ChangeVehicleConclusion = {
    applicationDate: quoteDate.value,
    changeVehicleRequest: {
      policyId: props.policyId,
      insuranceStartsAt: props.changeVehicleSearch.desiredStartDate,
      vehicleMileage: props.changeVehicleSearch.mileageAtTheBeginning,
      vehicleFirstRegistration: props.changeVehicleSearch.vehicleFirstRegistration,
      vehicleAcquiredAt: props.changeVehicleSearch.vehicleFirstRegistrationForPartner,
      vehicleTypeKey: props.changeVehicleSearch.typeKey,
      vehicleManufacturerId: props.changeVehicleSearch.manufacturerId,
      numberOfFullyComprehensiveInsuranceClaimsCurrentYear:
        NUMBER_OF_FULLY_COMPREHENSIVE_INSURANCE_CLAIMS_CURRENT_YEAR,
      numberOfLiabilityClaimsCurrentYear: NUMBER_OF_LIABILITY_CLAIMS_CURRENT_YEAR,
      circleOfDrivers: circleOfDrivers.value!
    }
  }

  emit('scroll-to-top')

  submitMotorConclusion(body)
    .then((motorConclusionResponse) => {
      emit('conclude', motorConclusionResponse)
    })
    .catch(() => {
      hasError.value = true
    })
    .finally(() => {
      loading.value = false
    })
}

function abort() {
  emit('abort')
}

function editVehicle() {
  emit('edit-vehicle')
}
</script>

<style scoped lang="scss">
.infotext {
  color: var(--primary-color-0);
}

// Ui Lib Fix
.alert > .text {
  color: var(--alert-font-color);
  font-family: var(--f-primary-bold);
  font-size: var(--base-font-size);
  line-height: 26px;
}

// Ui Lib Fix
.alert-regular-font {
  font-family: var(--f-primary-base) !important;
  font-weight: 400 !important;
}

.document-notice {
  background: var(--accent-color-3);
  padding: calc(var(--baseline) * 1.75) calc(var(--grid-gutter-width) * 1.5)
    calc(var(--baseline) * 1.75) calc(var(--grid-gutter-width) * 1.5);

  span {
    color: var(--primary-color-0);
  }
}

:deep(.hoverable-icon-span) {
  display: none !important;
}

:deep(.card__footer--sm) {
  .footer-cta {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    row-gap: 24px;
    text-align: var(--align-footer-items);
  }

  button.button {
    text-align: var(--align-footer-items) !important;
    width: 100% !important;
  }
}
</style>
